import React, { useEffect, useState, useCallback } from 'react';
import { Save, Loader2, Layout, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Group definitions keep the admin form scannable. Each group is rendered
// as its own card. Field types: 'text' (single-line), 'textarea' (longer
// helper/hint copy). The keys MUST match the backend DEFAULT_OF_LAYOUT
// dict — server silently drops unknown keys for safety.
const GROUPS = [
  {
    title: 'Page header',
    desc: 'Hero strip — title, subtitle and the 3 stat counters.',
    fields: [
      { key: 'page_title',         label: 'Page title',        type: 'text' },
      { key: 'page_subtitle',      label: 'Page subtitle',     type: 'textarea' },
      { key: 'stat_total_label',   label: 'Stat — total files label', type: 'text' },
      { key: 'stat_added_label',   label: 'Stat — added today label', type: 'text' },
      { key: 'stat_sync_label',    label: 'Stat — last sync label',   type: 'text' },
    ],
  },
  {
    title: 'Block 1 — Quick search',
    desc: 'The primary free-text search input. Most customers use only this field.',
    fields: [
      { key: 'block1_title',       label: 'Block title',       type: 'text' },
      { key: 'block1_badge',       label: 'Badge text',        type: 'text' },
      { key: 'block1_hint',        label: 'Hint / helper copy', type: 'textarea' },
      { key: 'block1_placeholder', label: 'Input placeholder', type: 'text' },
    ],
  },
  {
    title: 'Block 2 — Vehicle filters',
    desc: 'Brand · Model · Engine cascading selectors.',
    fields: [
      { key: 'block2_title',         label: 'Block title',     type: 'text' },
      { key: 'block2_hint',          label: 'Hint',            type: 'textarea' },
      { key: 'label_brand',          label: 'Brand label',     type: 'text' },
      { key: 'placeholder_brand',    label: 'Brand placeholder', type: 'text' },
      { key: 'label_model',          label: 'Model label',     type: 'text' },
      { key: 'placeholder_model',    label: 'Model placeholder', type: 'text' },
      { key: 'label_engine',         label: 'Engine label',    type: 'text' },
      { key: 'placeholder_engine',   label: 'Engine placeholder', type: 'text' },
    ],
  },
  {
    title: 'Block 3 — ECU control unit',
    desc: 'ECU brand + ECU model dropdowns and HW/SW/upgrade/calibration/damos number inputs.',
    fields: [
      { key: 'block3_title',          label: 'Block title',     type: 'text' },
      { key: 'block3_hint',           label: 'Hint',            type: 'textarea' },
      { key: 'label_ecu_brand',       label: 'ECU brand label', type: 'text' },
      { key: 'placeholder_ecu_brand', label: 'ECU brand placeholder', type: 'text' },
      { key: 'label_ecu_model',       label: 'ECU model label', type: 'text' },
      { key: 'placeholder_ecu_model', label: 'ECU model placeholder (disabled state)', type: 'text' },
      { key: 'label_hw',              label: 'HW number label', type: 'text' },
      { key: 'placeholder_hw',        label: 'HW number placeholder', type: 'text' },
      { key: 'label_sw',              label: 'SW number label', type: 'text' },
      { key: 'placeholder_sw',        label: 'SW number placeholder', type: 'text' },
      { key: 'label_sw_upgrade',      label: 'SW upgrade label', type: 'text' },
      { key: 'placeholder_sw_upgrade', label: 'SW upgrade placeholder', type: 'text' },
      { key: 'label_calibration',     label: 'Calibration label', type: 'text' },
      { key: 'placeholder_calibration', label: 'Calibration placeholder', type: 'text' },
      { key: 'label_damos',           label: 'Damos label (admin-only)', type: 'text' },
      { key: 'placeholder_damos',     label: 'Damos placeholder', type: 'text' },
    ],
  },
  {
    title: 'Action bar & results',
    desc: 'Search / Reset buttons, results counter and empty-state copy.',
    fields: [
      { key: 'btn_search',           label: 'Search button',     type: 'text' },
      { key: 'btn_reset',            label: 'Reset button',      type: 'text' },
      { key: 'btn_buy',              label: 'Buy button (in result row)', type: 'text' },
      { key: 'result_count_singular', label: 'Result counter — "1 match" word', type: 'text' },
      { key: 'result_count_plural',   label: 'Result counter — "N matches" word', type: 'text' },
      { key: 'empty_state',          label: 'Empty-state message', type: 'textarea' },
    ],
  },
  {
    title: 'Notices (buy modal & results footer)',
    desc: 'Short factual lines that prevent support questions. The first three appear in the buy modal, the last two under the result list.',
    fields: [
      { key: 'notice_no_refund',    label: 'No refunds — check identifiers', type: 'textarea' },
      { key: 'notice_file_size',    label: 'File size differs per tool',     type: 'textarea' },
      { key: 'notice_formats',      label: 'No FULL BACKUP / EPROM / SGO / VBF', type: 'textarea' },
      { key: 'notice_delivery_zip', label: 'Delivered as ZIP archive',       type: 'textarea' },
      { key: 'notice_extension',    label: '.ori extension / rename to .bin', type: 'textarea' },
    ],
  },
];

export default function OriginalFilesLayoutEditor({ token }) {
  const [values, setValues] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  const auth = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/original-files-layout`);
      if (!r.ok) return;
      const data = await r.json();
      setValues(data || {});
      setDefaults(data || {});
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/original-files-layout`, {
        method: 'PUT', headers: auth, body: JSON.stringify(values),
      });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      const data = await r.json();
      setValues(data || values);
      setFlash(true);
      setTimeout(() => setFlash(false), 1800);
    } finally { setSaving(false); }
  };

  const reset = (key) => setValues((v) => ({ ...v, [key]: defaults[key] || '' }));

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading layout config…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="orig-layout-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <Layout className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900">
          <p className="font-semibold mb-1">Original Files page — labels & copy</p>
          <p className="text-cyan-800">
            Customize every label, placeholder, hint and button on the customer-facing
            Original Files page. Changes go live immediately for new visitors.
          </p>
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="mb-3">
            <h3 className="text-base font-bold text-slate-800">{g.title}</h3>
            <p className="text-xs text-slate-500">{g.desc}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {g.fields.map((f) => (
              <div
                key={f.key}
                className={f.type === 'textarea' ? 'sm:col-span-2' : ''}
              >
                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
                  {f.label}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    value={values[f.key] || ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    rows={2}
                    data-testid={`of-input-${f.key}`}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                  />
                ) : (
                  <input
                    type="text"
                    value={values[f.key] || ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    data-testid={`of-input-${f.key}`}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Sticky save bar — keeps the Save button reachable even on the
          long form without having to scroll back to the top. */}
      <div className="sticky bottom-4 z-10 flex items-center gap-2 bg-white rounded-xl border border-slate-200 shadow-lg p-3">
        <span className="text-xs text-slate-500 pl-1">
          {Object.keys(values).length} settings · changes apply instantly after Save
        </span>
        {flash && <span className="text-emerald-600 text-sm font-medium" data-testid="of-layout-saved">✓ Saved</span>}
        <span className="flex-1" />
        <button
          onClick={() => load()}
          className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium flex items-center gap-2"
          data-testid="of-layout-discard"
        >
          <RotateCcw className="w-4 h-4" /> Discard
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="of-layout-save"
          className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-semibold disabled:opacity-50 hover:shadow-md flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </button>
      </div>
    </div>
  );
}
